import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    getERASettings,
    updateERASettings,
    listCustomPaymentRules,
    createCustomPaymentRule,
    patchCustomPaymentRule,
    deleteCustomPaymentRule,
    bulkUpdateDenialPriorities,
    normalizeRulesListResponse,
    formatApiError,
} from "./service";
import {
    RULE_TYPE_ADJUSTMENT,
    RULE_TYPE_DENIAL_ROUTING,
} from "./constants";
import i18n from "../../../utilities/i18n";

const isOk = (status) => status === 200 || status === 201;
const isDeleteOk = (status) => isOk(status) || status === 204;

const commonErrorPayload = (error) => ({
    message: error?.message || i18n.t("errorMessages.commonError"),
});

// Keeps custom rule mutation thunks consistent while allowing each thunk to map its own success payload.
const handleApiCall = async (
    apiFunc,
    rejectWithValue,
    {
        isSuccess = (response) => isOk(response?.status),
        mapResponse = (response) => response.data,
        includeRawError = false,
    } = {}
) => {
    try {
        const response = await apiFunc();
        if (!isSuccess(response)) {
            const payload = {
                message: formatApiError(response?.data),
            };
            if (includeRawError) {
                payload.raw = response?.data;
            }
            return rejectWithValue(payload);
        }
        return mapResponse(response);
    } catch (error) {
        return rejectWithValue(commonErrorPayload(error));
    }
};

const defaultSettings = {
    is_enabled: false,
    excluded_payment_modes: [],
    post_after_days: 0,
};

const initialCustomRules = {
    rulesLoading: false,
    rulesSaving: false,
    mutationError: null,
    rules: [],
    selectedAdjustmentId: null,
    selectedDenialId: null,
};

// Preserve the current rule selection after refreshes unless that rule no longer exists.
const pickDefaultSelections = (rules, state) => {
    const adjustments = rules.filter((r) => r.rule_type === RULE_TYPE_ADJUSTMENT);
    const denials = rules.filter((r) => r.rule_type === RULE_TYPE_DENIAL_ROUTING);
    const adjIds = new Set(adjustments.map((r) => r.id));
    const denIds = new Set(denials.map((r) => r.id));

    if (
        state.selectedAdjustmentId == null ||
        !adjIds.has(state.selectedAdjustmentId)
    ) {
        state.selectedAdjustmentId =
            adjustments.length > 0 ? adjustments[0].id : null;
    }
    if (
        state.selectedDenialId == null ||
        !denIds.has(state.selectedDenialId)
    ) {
        state.selectedDenialId = denials.length > 0 ? denials[0].id : null;
    }
};

// Keep an explicit user selection across refreshes, but do not auto-open a rule
// when the page is first loaded and nothing has been selected yet.
const preserveExistingSelections = (rules, state) => {
    const adjIds = new Set(
        rules
            .filter((r) => r.rule_type === RULE_TYPE_ADJUSTMENT)
            .map((r) => r.id)
    );
    const denIds = new Set(
        rules
            .filter((r) => r.rule_type === RULE_TYPE_DENIAL_ROUTING)
            .map((r) => r.id)
    );

    if (
        state.selectedAdjustmentId != null &&
        !adjIds.has(state.selectedAdjustmentId)
    ) {
        state.selectedAdjustmentId = null;
    }
    if (state.selectedDenialId != null && !denIds.has(state.selectedDenialId)) {
        state.selectedDenialId = null;
    }
};

export const fetchERASettings = createAsyncThunk(
    "eraAutomation/fetch",
    async (practicePk, { rejectWithValue }) => {
        try {
            const response = await getERASettings(practicePk);
            if (isOk(response?.status) && response?.data) {
                return response.data;
            }
            if (response?.status === 403) {
                return rejectWithValue({ accessDenied: true });
            }
            return rejectWithValue({
                message:
                    response?.error_data?.message ||
                    i18n.t("errorMessages.commonError"),
            });
        } catch (error) {
            return rejectWithValue({
                message: error?.message || i18n.t("errorMessages.commonError"),
            });
        }
    }
);

export const saveERASettings = createAsyncThunk(
    "eraAutomation/save",
    async ({ practicePk, data }, { rejectWithValue }) => {
        try {
            const response = await updateERASettings(practicePk, data);
            if (isOk(response?.status) && response?.data) {
                return response.data;
            }
            if (response?.status === 403) {
                return rejectWithValue({ accessDenied: true });
            }
            return rejectWithValue({
                message:
                    response?.data?.message ||
                    i18n.t("errorMessages.commonError"),
            });
        } catch (error) {
            return rejectWithValue({
                message: error?.message || i18n.t("errorMessages.commonError"),
            });
        }
    }
);

export const fetchCustomPaymentRules = createAsyncThunk(
    "eraAutomation/fetchCustomRules",
    async (practicePk, { rejectWithValue }) => {
        try {
            const response = await listCustomPaymentRules(practicePk, 0);
            if (response?.status === 403) {
                return rejectWithValue({ accessDenied: true });
            }
            if (!isOk(response?.status)) {
                return rejectWithValue({
                    message: formatApiError(response?.data),
                });
            }
            const rules = normalizeRulesListResponse(response.data);
            return rules;
        } catch (error) {
            return rejectWithValue({
                message: error?.message || i18n.t("errorMessages.commonError"),
            });
        }
    }
);

export const createRuleThunk = createAsyncThunk(
    "eraAutomation/createRule",
    async ({ practicePk, body }, { rejectWithValue }) =>
        handleApiCall(
            () => createCustomPaymentRule(practicePk, body),
            rejectWithValue,
            { includeRawError: true }
        )
);

export const patchRuleThunk = createAsyncThunk(
    "eraAutomation/patchRule",
    async ({ practicePk, ruleId, body }, { rejectWithValue }) =>
        handleApiCall(
            () => patchCustomPaymentRule(practicePk, ruleId, body),
            rejectWithValue,
            { includeRawError: true }
        )
);

export const deleteRuleThunk = createAsyncThunk(
    "eraAutomation/deleteRule",
    async ({ practicePk, ruleId }, { rejectWithValue }) =>
        handleApiCall(
            () => deleteCustomPaymentRule(practicePk, ruleId),
            rejectWithValue,
            {
                isSuccess: (response) => isDeleteOk(response?.status),
                mapResponse: () => ({ ruleId }),
            }
        )
);

export const bulkUpdateDenialPrioritiesThunk = createAsyncThunk(
    "eraAutomation/bulkPriority",
    async ({ practicePk, priorityData }, { rejectWithValue }) => {
        const body = {
            current_practice_id: Number(practicePk),
            priority_data: priorityData,
        };
        return handleApiCall(
            () => bulkUpdateDenialPriorities(practicePk, body),
            rejectWithValue,
            { mapResponse: () => true }
        );
    }
);

const baseInitialState = {
    settings: defaultSettings,
    settingsLoaded: false,
    loading: false,
    saving: false,
    accessDenied: false,
    error: null,
    customRules: { ...initialCustomRules },
};

const eraAutomationSlice = createSlice({
    name: "eraAutomation",
    initialState: baseInitialState,
    reducers: {
        resetERAState: () => ({
            ...baseInitialState,
            customRules: {
                ...initialCustomRules,
            },
        }),
        setSelectedAdjustmentId: (state, action) => {
            state.customRules.selectedAdjustmentId = action.payload;
        },
        setSelectedDenialId: (state, action) => {
            state.customRules.selectedDenialId = action.payload;
        },
        clearCustomRulesMutationError: (state) => {
            state.customRules.mutationError = null;
        },
        /** orderedDenialIds: full denial rule ids in new order (length = all denials) */
        optimisticDenialReorder: (state, action) => {
            const orderedIds = action.payload;
            const idToPriority = new Map(
                orderedIds.map((id, i) => [id, i + 1])
            );
            state.customRules.rules.forEach((r) => {
                if (r.rule_type === RULE_TYPE_DENIAL_ROUTING) {
                    const p = idToPriority.get(r.id);
                    if (p != null) r.denial_routing_priority = p;
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchERASettings.pending, (state) => {
                state.loading = true;
                state.settingsLoaded = false;
                state.error = null;
                state.accessDenied = false;
            })
            .addCase(fetchERASettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settingsLoaded = true;
                state.settings = {
                    is_enabled: !!action.payload.is_enabled,
                    excluded_payment_modes:
                        action.payload.excluded_payment_modes || [],
                    post_after_days: action.payload.post_after_days ?? 0,
                };
            })
            .addCase(fetchERASettings.rejected, (state, action) => {
                state.loading = false;
                state.settingsLoaded = true;
                state.accessDenied = !!action.payload?.accessDenied;
                state.error = action.payload?.message || null;
            })
            .addCase(saveERASettings.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(saveERASettings.fulfilled, (state, action) => {
                state.saving = false;
                state.settings = {
                    is_enabled: !!action.payload.is_enabled,
                    excluded_payment_modes:
                        action.payload.excluded_payment_modes || [],
                    post_after_days: action.payload.post_after_days ?? 0,
                };
            })
            .addCase(saveERASettings.rejected, (state, action) => {
                state.saving = false;
                state.accessDenied = !!action.payload?.accessDenied;
                state.error = action.payload?.message || null;
            })
            .addCase(fetchCustomPaymentRules.pending, (state) => {
                state.customRules.rulesLoading = true;
                state.customRules.mutationError = null;
            })
            .addCase(fetchCustomPaymentRules.fulfilled, (state, action) => {
                state.customRules.rulesLoading = false;
                state.customRules.rules = action.payload;
                preserveExistingSelections(action.payload, state.customRules);
            })
            .addCase(fetchCustomPaymentRules.rejected, (state, action) => {
                state.customRules.rulesLoading = false;
                state.customRules.mutationError =
                    action.payload?.message || null;
                state.accessDenied = !!action.payload?.accessDenied;
            })
            .addCase(createRuleThunk.pending, (state) => {
                state.customRules.rulesSaving = true;
                state.customRules.mutationError = null;
            })
            .addCase(createRuleThunk.fulfilled, (state, action) => {
                state.customRules.rulesSaving = false;
                const created = action.payload;
                state.customRules.rules.push(created);
                if (created.rule_type === RULE_TYPE_ADJUSTMENT) {
                    state.customRules.selectedAdjustmentId = created.id;
                }
                if (created.rule_type === RULE_TYPE_DENIAL_ROUTING) {
                    state.customRules.selectedDenialId = created.id;
                }
            })
            .addCase(createRuleThunk.rejected, (state, action) => {
                state.customRules.rulesSaving = false;
                state.customRules.mutationError =
                    action.payload?.message || null;
            })
            .addCase(patchRuleThunk.pending, (state) => {
                state.customRules.rulesSaving = true;
                state.customRules.mutationError = null;
            })
            .addCase(patchRuleThunk.fulfilled, (state, action) => {
                state.customRules.rulesSaving = false;
                const updated = action.payload;
                const idx = state.customRules.rules.findIndex(
                    (r) => r.id === updated.id
                );
                if (idx >= 0) state.customRules.rules[idx] = updated;
            })
            .addCase(patchRuleThunk.rejected, (state, action) => {
                state.customRules.rulesSaving = false;
                state.customRules.mutationError =
                    action.payload?.message || null;
            })
            .addCase(deleteRuleThunk.pending, (state) => {
                state.customRules.rulesSaving = true;
                state.customRules.mutationError = null;
            })
            .addCase(deleteRuleThunk.fulfilled, (state, action) => {
                state.customRules.rulesSaving = false;
                const { ruleId } = action.payload;
                state.customRules.rules = state.customRules.rules.filter(
                    (r) => r.id !== ruleId
                );
                pickDefaultSelections(state.customRules.rules, state.customRules);
            })
            .addCase(deleteRuleThunk.rejected, (state, action) => {
                state.customRules.rulesSaving = false;
                state.customRules.mutationError =
                    action.payload?.message || null;
            })
            .addCase(bulkUpdateDenialPrioritiesThunk.pending, (state) => {
                state.customRules.rulesSaving = true;
                state.customRules.mutationError = null;
            })
            .addCase(bulkUpdateDenialPrioritiesThunk.fulfilled, (state) => {
                state.customRules.rulesSaving = false;
            })
            .addCase(bulkUpdateDenialPrioritiesThunk.rejected, (state, action) => {
                state.customRules.rulesSaving = false;
                state.customRules.mutationError =
                    action.payload?.message || null;
            });
    },
});

export const {
    resetERAState,
    setSelectedAdjustmentId,
    setSelectedDenialId,
    clearCustomRulesMutationError,
    optimisticDenialReorder,
} = eraAutomationSlice.actions;

export default eraAutomationSlice.reducer;
